import { useId, useState } from 'react';
import { userHandle } from '../../api/types';
import type { Id, Project, Sprint, StatusDef } from '../../api/types';
import { useCurrentUser } from '../../auth/AuthProvider';

export interface Filters {
  projectId: Id | null;
  status: string | null;
  assignee: string | null;
  sprintId: Id | null;
}

export const NO_FILTERS: Filters = {
  projectId: null,
  status: null,
  assignee: null,
  sprintId: null,
};

const SELECT_CLASS =
  'h-9 min-w-0 rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 text-sm text-[color:var(--color-ink)]';

const BUTTON_CLASS =
  'h-9 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10';

/**
 * ⭐ Labelled groups rather than a flat list, because a sprint's STATE is what decides whether it is
 * the one you meant. `<optgroup>` is the native mechanism — it survives being rendered as a system
 * picker on a phone, which a styled div-list would not.
 * ⛔ The array is in the API's own order; it does not re-sort, it names the order already there.
 */
const SPRINT_GROUPS = [
  { state: 'active', label: 'Active' },
  { state: 'planned', label: 'Planned' },
  { state: 'closed', label: 'Closed' },
] as const;

/**
 * `APFS v1.0.33 · v1.0.33` — the sprint, and the release it ships as when db/001's
 * "sprint-is-the-release link" is set. ⚠️ Dates are deliberately NOT here: they are `YYYY-MM-DD`
 * strings that must not be `new Date()`-ed (see `Sprint`), and a picker on a 390px phone has no room
 * for a range that is not what anyone is choosing by.
 */
function sprintOptionLabel(sprint: Sprint): string {
  return sprint.version_name ? `${sprint.name} · ${sprint.version_name}` : sprint.name;
}

export function countActive(filters: Filters): number {
  return [filters.projectId, filters.status, filters.assignee, filters.sprintId].filter(Boolean)
    .length;
}

/**
 * Project · status · assignee.
 *
 * 🔴 COLLAPSED BY DEFAULT ON A PHONE. Laid out as a stack of three full-width controls it took ~260px
 * of an 844px screen before the first ticket — a third of the viewport spent on a control people
 * touch once a session, on the screen where the LIST is the product. Desktop has the width for a
 * toolbar and keeps it. Same component, two layouts, because the constraint genuinely differs.
 *
 * ⭐ The trigger carries the ACTIVE COUNT, so a collapsed panel can never hide the fact that the list
 * is filtered — which is the one thing collapsing it could otherwise cost.
 */
export function FilterBar({
  projects,
  statuses,
  assigneeSuggestions,
  sprints,
  sprintsUnavailable,
  filters,
  onChange,
  disabled,
}: {
  projects: Project[];
  statuses: StatusDef[];
  assigneeSuggestions: string[];
  sprints: Sprint[];
  /** The sprint list was asked for and FAILED — distinct from "no project chosen, so none asked". */
  sprintsUnavailable?: boolean;
  filters: Filters;
  onChange: (next: Filters) => void;
  disabled?: boolean;
}) {
  const currentUser = useCurrentUser();
  const listId = useId();
  const panelId = useId();
  const [expanded, setExpanded] = useState(false);
  const activeStatuses = statuses.filter((status) => status.is_active !== false);
  const active = countActive(filters);
  const clear = () => onChange(NO_FILTERS);
  // 🔴 Sprints are per-project and there is no all-projects form — see `fetchSprints`.
  const sprintReady = Boolean(filters.projectId) && !sprintsUnavailable;

  return (
    <div className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
      <div className="flex items-center justify-between gap-2 px-3 py-2 sm:hidden">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded((value) => !value)}
          className={BUTTON_CLASS}
        >
          Filters
          {active > 0 ? (
            <span className="ml-1.5 inline-flex size-5 items-center justify-center rounded-full bg-[color:var(--color-accent)] text-[11px] font-bold text-[color:var(--color-on-accent)]">
              {active}
            </span>
          ) : null}
          <span aria-hidden="true" className="ml-1.5">
            {expanded ? '▴' : '▾'}
          </span>
        </button>
        {active > 0 ? (
          <button type="button" onClick={clear} className={BUTTON_CLASS}>
            Clear
          </button>
        ) : null}
      </div>

      <div
        id={panelId}
        className={`${expanded ? 'flex' : 'hidden'} flex-col gap-2 px-3 pb-2.5 sm:flex sm:flex-row sm:flex-wrap sm:items-center sm:px-4 sm:py-2.5`}
      >
        <label className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-1.5">
          <span className="text-xs font-semibold text-[color:var(--color-ink-muted)]">Project</span>
          <select
            className={SELECT_CLASS}
            disabled={disabled}
            value={filters.projectId ?? ''}
            onChange={(event) =>
              onChange({ ...filters, projectId: event.target.value || null })
            }
          >
            <option value="">All projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.key} — {project.name}
              </option>
            ))}
          </select>
        </label>

        {/* ⭐ Directly after Project, because it is a NARROWING of it: the dependent control sits
            next to the thing it depends on, which is what makes its disabled state legible rather
            than mysterious. Jira reaches the same place differently — its board is already one
            project, so its sprint is the group header rather than a filter. */}
        <label className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-1.5">
          <span className="text-xs font-semibold text-[color:var(--color-ink-muted)]">Sprint</span>
          <select
            className={SELECT_CLASS}
            disabled={disabled || !sprintReady || sprints.length === 0}
            value={filters.sprintId ?? ''}
            onChange={(event) => onChange({ ...filters, sprintId: event.target.value || null })}
          >
            {/* ⚠️ FOUR DIFFERENT REASONS THIS CONTROL CAN BE EMPTY, and they are not the same fact.
                A single "Any sprint" for all of them would tell someone whose sprint list failed to
                load that their project has no sprints. */}
            <option value="">
              {!filters.projectId
                ? 'Choose a project first'
                : sprintsUnavailable
                  ? 'Sprints unavailable'
                  : sprints.length === 0
                    ? 'No sprints in this project'
                    : 'Any sprint'}
            </option>
            {/* ⛔ Rendered in the SERVER'S order — active, planned soonest-first, closed
                most-recent-first. The optgroups label that order; they do not re-impose one. */}
            {SPRINT_GROUPS.map(({ state, label }) => {
              const group = sprints.filter((sprint) => sprint.state === state);
              if (group.length === 0) return null;
              return (
                <optgroup key={state} label={label}>
                  {group.map((sprint) => (
                    <option key={sprint.id} value={sprint.id}>
                      {sprintOptionLabel(sprint)}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </label>

        <label className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-1.5">
          <span className="text-xs font-semibold text-[color:var(--color-ink-muted)]">Status</span>
          <select
            className={SELECT_CLASS}
            disabled={disabled || activeStatuses.length === 0}
            value={filters.status ?? ''}
            onChange={(event) => onChange({ ...filters, status: event.target.value || null })}
          >
            <option value="">Any status</option>
            {activeStatuses.map((status) => (
              <option key={status.code} value={status.code}>
                {status.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-1.5">
          <span className="text-xs font-semibold text-[color:var(--color-ink-muted)]">Assignee</span>
          <input
            className={SELECT_CLASS}
            disabled={disabled}
            list={listId}
            placeholder="GitLab username"
            value={filters.assignee ?? ''}
            onChange={(event) => onChange({ ...filters, assignee: event.target.value || null })}
          />
          {/* ⚠️ Suggestions are the assignees ON THIS PAGE only — there is no members endpoint yet, so
              this cannot claim to be the full team list. Free text still works for anyone else. */}
          <datalist id={listId}>
            {assigneeSuggestions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </label>

        <div className="flex gap-2 sm:ml-auto">
          {currentUser ? (
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange({ ...filters, assignee: userHandle(currentUser) })}
              className={BUTTON_CLASS}
            >
              Only mine
            </button>
          ) : null}
          <button type="button" disabled={disabled} onClick={clear} className={BUTTON_CLASS}>
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ⚠️ The sprint is named by LOOKUP, and falls back to the raw id rather than being omitted. A URL can
 * carry `?sprintId=12` for a sprint not in the loaded list — then "No tickets match this filter"
 * would name every filter EXCEPT the one doing the filtering, which is the shape of an empty list
 * nobody can account for.
 */
export function describeFilters(
  filters: Filters,
  projects: Project[],
  statusLabel: string | null,
  sprints: Sprint[] = [],
): string {
  const parts: string[] = [];
  const project = projects.find((entry) => entry.id === filters.projectId);
  if (project) parts.push(`project ${project.key}`);
  if (filters.sprintId) {
    const sprint = sprints.find((entry) => entry.id === filters.sprintId);
    parts.push(`sprint ${sprint ? sprint.name : filters.sprintId}`);
  }
  if (statusLabel) parts.push(`status ${statusLabel}`);
  if (filters.assignee) parts.push(`assignee ${filters.assignee}`);
  return parts.join(', ');
}
